import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/solid";
import _ from "lodash";
import { useEffect, useState } from "react";
import { SketchPicker } from "react-color";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { PrimaryButton } from "../components/Buttons";
import {
  RadioInput,
  RadioInputGroup,
  SelectInput,
  TextInput,
} from "../components/Inputs";
import { EnumEntityModal, StandardEntityModal } from "../components/Modals";
import { PrimaryTable } from "../components/Tables";
import { useLoaderStore } from "../context";
import { useAlert, useApi } from "../hooks";
import {
  BACKEND_FRAMEWORKS,
  DATABASES,
  FRONTEND_FRAMEWORKS,
} from "../utils/frameworks";
import constants from "../utils/constants";

const NEW_ENUM_ENTITY = {
  type: "enum",
  name: "",
  enum: {
    values: [],
  },
  isUserManaged: false,
  isSoftDeletable: false,
  properties: [
    {
      name: "Name",
      type: 0,
      isNullable: false,
      maxLength: 30,
    },
  ],
};

const NEW_STANDARD_ENTITY = {
  type: "standard",
  name: "",
  isSoftDeletable: true,
  isUserManaged: true,
  domain: constants.DOMAINS[0].value,
  includeInApiSpike: false,
  properties: [],
};

const EnumDisplayTable = ({ entity }) => {
  return (
    <PrimaryTable
      paginatorHidden
      header={
        <PrimaryTable.Header>
          <PrimaryTable.HeaderColumn display="Name" />
          <PrimaryTable.HeaderColumn display="Value" />
          <PrimaryTable.HeaderColumn display="Description" />
        </PrimaryTable.Header>
      }
    >
      {_.map(entity?.enum?.values, (x) => (
        <PrimaryTable.Row key={`${x.value}`}>
          <PrimaryTable.Cell>{x.name}</PrimaryTable.Cell>
          <PrimaryTable.Cell>{x.value}</PrimaryTable.Cell>
          <PrimaryTable.Cell>{x.description}</PrimaryTable.Cell>
        </PrimaryTable.Row>
      ))}
    </PrimaryTable>
  );
};

const StandardEntityDisplayTable = ({ entity }) => {
  return (
    <PrimaryTable
      paginatorHidden
      header={
        <PrimaryTable.Header>
          <PrimaryTable.HeaderColumn display="User managed" />
          <PrimaryTable.HeaderColumn display="Soft deletable" />
          <PrimaryTable.HeaderColumn display="Include in API spike" />
          <PrimaryTable.HeaderColumn display="Domain name" />
          <PrimaryTable.HeaderColumn display="Property count" />
        </PrimaryTable.Header>
      }
    >
      <PrimaryTable.Row>
        <PrimaryTable.Cell>
          {entity.isUserManaged ? "YES" : "NO"}
        </PrimaryTable.Cell>
        <PrimaryTable.Cell>
          {entity.isSoftDeletable ? "YES" : "NO"}
        </PrimaryTable.Cell>
        <PrimaryTable.Cell>
          {entity.includeInApiSpike ? "YES" : "NO"}
        </PrimaryTable.Cell>
        <PrimaryTable.Cell>
          {entity.includeInApiSpike ? entity.domain?.label : "N/A"}
        </PrimaryTable.Cell>
        <PrimaryTable.Cell>{entity.properties?.length}</PrimaryTable.Cell>
      </PrimaryTable.Row>
    </PrimaryTable>
  );
};

export default function Generator() {
  const alert = useAlert();
  const navigate = useNavigate();
  const { post } = useApi();
  const { setShowLoader } = useLoaderStore();

  const [projectName, setProjectName] = useState("");
  const [repositoryName, setRepositoryName] = useState("");
  const [clientName, setClientName] = useState("");
  const [backendFramework, setBackendFramework] = useState(
    BACKEND_FRAMEWORKS[0]
  );
  const [frontendFramework, setFrontendFramework] = useState(
    FRONTEND_FRAMEWORKS[0]
  );
  const [database, setDatabase] = useState(DATABASES[0]);
  const [primaryColor, setPrimaryColor] = useState("#253da1");
  const [downloadAsZipFile, setDownloadAsZipFile] = useState(false);
  const [entities, setEntities] = useState([]);
  const [newEntityType, setNewEntityType] = useState("standard");
  const [enumEntityModalOpen, setEnumEntityModalOpen] = useState(false);
  const [standardEntityModalOpen, setStandardEntityModalOpen] = useState(false);
  const [currentEnumEntity, setCurrentEnumEntity] = useState({
    ...NEW_ENUM_ENTITY,
    id: uuidv4(),
  });
  const [currentStandardEntity, setCurrentStandardEntity] = useState({
    ...NEW_STANDARD_ENTITY,
    id: uuidv4(),
  });
  const [domains, setDomains] = useState(constants.DOMAINS);

  useEffect(() => {
    if (!standardEntityModalOpen)
      setCurrentStandardEntity({
        ...NEW_STANDARD_ENTITY,
        id: uuidv4(),
      });
  }, [standardEntityModalOpen]);

  const handleSubmit = async () => {
    if (!isValid()) return;

    setShowLoader(true);

    await post("generator/GenerateApp", {
      projectName,
      repositoryName,
      clientName,
      backendFramework: backendFramework.value,
      frontendFramework: frontendFramework.value,
      database: database.value,
      primaryColor,
      entities,
      downloadAsZipFile,
    })
      .then(() => {
        alert("Success!", "Successfully created app", "success");
        navigate("/");
      })
      .catch((err) => {
        alert(
          "Unsuccessful",
          err.data.message || err.data.toString() || "Unable to generate project",
          "error"
        );
        console.error(err);
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const isValid = () => {
    if (!projectName || !projectName.trim()) {
      alert("Invalid input", "Please enter a valid project name", "warning");
      return false;
    }
    if (!downloadAsZipFile && (!repositoryName || !repositoryName.trim())) {
      alert("Invalid input", "Please enter a valid repository name", "warning");
      return false;
    }
    if (!clientName || !clientName.trim()) {
      alert("Invalid input", "Please enter a valid client name", "warning");
      return false;
    }
    if (!frontendFramework) {
      alert("Invalid input", "Please enter a frontend framework", "warning");
      return false;
    }
    if (!backendFramework) {
      alert("Invalid input", "Please enter a backend framework", "warning");
      return false;
    }
    if (!database) {
      alert("Invalid input", "Please enter a database type", "warning");
      return false;
    }
    if (!primaryColor) {
      alert("Invalid input", "Please enter a valid color", "warning");
      return false;
    }
    return true;
  };

  const handleAddEntity = (entity) => {
    let existingEntity = entities.find((x) => x.id === entity.id);
    if (!existingEntity) {
      setEntities([...entities, entity]);
    } else {
      setEntities((prevEntities) => [
        ...prevEntities.filter((x) => x.id !== entity.id),
        entity,
      ]);
    }

    setCurrentEnumEntity({ ...NEW_ENUM_ENTITY, id: uuidv4() });
    setCurrentStandardEntity({ ...NEW_STANDARD_ENTITY, id: uuidv4() });
  };

  return (
    <>
      {/* Project information */}
      <h4 className="text-xl mt-8 px-4 w-full border-b">Project information</h4>
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 lg:gap-x-8 gap-y-4">
        <div>
          <TextInput
            label="Project name"
            subLabel="This will be the name of the files in the project (ie SGB.API, SGB.Services, etc)"
            additonalLabel="When naming an Elixir/Phoenix project use Pascal Case (ie SgbApi)"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
          />
        </div>
        <div>
          <TextInput
            label="Client name"
            subLabel="Client's actual name (displayed in app)"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
          />
        </div>
        <div>
          <SelectInput
            options={BACKEND_FRAMEWORKS}
            label="Backend Framework"
            value={backendFramework}
            onChange={setBackendFramework}
          />
        </div>
        <div>
          <SelectInput
            options={FRONTEND_FRAMEWORKS}
            label="Frontend Framework"
            value={frontendFramework}
            onChange={setFrontendFramework}
          />
        </div>
        <div>
          <SelectInput
            options={DATABASES}
            label="Database"
            value={database}
            onChange={setDatabase}
          />
        </div>
        {downloadAsZipFile ? null : (
          <div>
            <TextInput
              label="Repository Name"
              subLabel="Name of repo created in Sightsource Github"
              value={repositoryName}
              onChange={(e) => setRepositoryName(e.target.value)}
            />
          </div>
        )}
      </div>
      {/* Project color */}
      <div className="px-4">
        <div
          className="text-sm text-gray-700 mb-1"
          label="Choose a Project Color"
        >
          Choose a Project Color
        </div>
        <SketchPicker
          color={primaryColor}
          onChangeComplete={(color) => setPrimaryColor(color.hex)}
        />
        <div
          className="mt-2 text-center rounded-md w-24 py-3 px-1 text-white border"
          style={{
            backgroundColor: `${primaryColor}`,
          }}
        >
          {primaryColor}
        </div>
      </div>

      {/* Entities */}
      <h4 className="text-xl mt-8 px-4 w-full border-b">
        Entities &#40;Tables&#41;
      </h4>
      <div className="px-4 italic text-gray-400">
        Note: when naming entities, use singular names -- not plural. Also just
        go with pascal naming convention. If you choose postgres, sgb will auto
        convert to snake case. Eg do FavoriteColor for an entity name
      </div>

      {/* New entity */}
      <div className="mt-6 px-4 font-semibold">
        <h6>Create new entity</h6>
        <div className="flex items-center">
          <RadioInputGroup onChange={setNewEntityType}>
            <RadioInput
              label="Standard"
              value="standard"
              groupName="tabletype"
              checked={newEntityType === "standard"}
            />
            <RadioInput
              label="Enum"
              value="enum"
              groupName="tableType"
              checked={newEntityType === "enum"}
            />
          </RadioInputGroup>
          <div className="w-48 ml-8">
            <PrimaryButton
              text={`Create ${newEntityType || ""} entity`}
              onClick={() => {
                if (newEntityType === "standard")
                  setStandardEntityModalOpen(true);
                else if (newEntityType === "enum") {
                  setEnumEntityModalOpen(true);
                  setCurrentEnumEntity({ ...NEW_ENUM_ENTITY, id: uuidv4() });
                }
              }}
            />
          </div>
        </div>
      </div>

      <EnumEntityModal
        entity={currentEnumEntity}
        entities={entities}
        open={enumEntityModalOpen}
        setOpen={setEnumEntityModalOpen}
        handleAddEntity={handleAddEntity}
      />

      <StandardEntityModal
        entity={currentStandardEntity}
        entities={entities}
        open={standardEntityModalOpen}
        setOpen={setStandardEntityModalOpen}
        handleAddEntity={handleAddEntity}
        domains={domains}
        setDomains={setDomains}
      />

      {/* Existing standard entities */}
      {entities.filter((x) => x.type === "standard")?.length > 0 && (
        <div className="px-4 mt-12">
          <h6 className="font-semibold mb-2 text-xl">Standard entities</h6>
          {entities
            .filter((x) => x.type === "standard")
            .map((x, i) => {
              return (
                <div className="mb-8">
                  <div className="text-lg font-semibold flex space-x-2 mb-2 items-center">
                    <div>{x.name}</div>{" "}
                    <div
                      onClick={() => {
                        setCurrentStandardEntity(x);
                        setStandardEntityModalOpen(true);
                      }}
                    >
                      <PencilSquareIcon className="w-5 cursor-pointer hover:text-gray-400" />
                    </div>
                    <TrashIcon
                      className="w-5 cursor-pointer text-red-900 hover:text-red-500"
                      onClick={() => {
                        setEntities((prevEntities) =>
                          prevEntities.filter((entity) => entity.id !== x.id)
                        );
                      }}
                    />
                  </div>
                  <div>
                    <StandardEntityDisplayTable entity={x} />
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {/* Existing enums */}
      {entities.filter((x) => x.type === "enum")?.length > 0 && (
        <div className="px-4 mt-12">
          <h6 className="font-semibold mb-2 text-xl">Enums</h6>
          {entities
            .filter((x) => x.type === "enum")
            .map((x, i) => {
              return (
                <div className="mb-8">
                  <div className="text-lg font-semibold flex space-x-2 mb-2 items-center">
                    <div>{x.name}</div>{" "}
                    <div
                      onClick={() => {
                        setCurrentEnumEntity(x);
                        setEnumEntityModalOpen(true);
                      }}
                    >
                      <PencilSquareIcon className="w-5 cursor-pointer hover:text-gray-400" />
                    </div>
                    <TrashIcon
                      className="w-5 cursor-pointer text-red-900 hover:text-red-500"
                      onClick={() => {
                        setEntities((prevEntities) =>
                          prevEntities.filter((entity) => entity.id !== x.id)
                        );
                      }}
                    />
                  </div>
                  <div>
                    <EnumDisplayTable entity={x} />
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {/* Generate button */}
      <div className="px-4 mt-12 mb-4">
        <PrimaryButton text="Generate" onClick={handleSubmit}></PrimaryButton>
      </div>
    </>
  );
}
